// components/DocumentManagementTab.jsx

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  DocumentArrowUpIcon, DocumentMagnifyingGlassIcon, DocumentTextIcon,
  XMarkIcon, EyeIcon, ArrowDownTrayIcon, TrashIcon, PencilIcon,
  CheckCircleIcon, ArrowPathIcon, SparklesIcon, LinkIcon,
  ExclamationTriangleIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';

import ExtractionResultModal from './ExtractionResultModal';
import ExtractionProgressModal from './ExtractionProgressModal';
import { documentsApi } from '../../../hooks/document/documentsApi';
import DataTable from '../../common/DataTable';

import {
  useDocuments, useUploadDocument, useUpdateDocument,
  useDeleteDocument, useDocumentPreview, useDownloadDocument, useExtractProposal,
} from '../../../hooks/document/useDocument';

// ─── Constants ────────────────────────────────────────────────────────────────

const DOCUMENT_CATEGORIES = [
  'RFP', 'Proposal', 'Contract', 'Reference',
  'Template', 'Report', 'Presentation', 'Other',
];
export const BID_STATUSES     = ['Pending', 'In Progress', 'Submitted', 'Won', 'Lost', 'Declined'];
export const ENGAGEMENT_TYPES = ['Project', 'Retainer', 'Consulting', 'Advisory'];
const AVAILABLE_TAGS = [
  'High Priority', 'Urgent', 'Legal Review', 'Finance Review',
  'Awaiting Client', 'Needs Follow-up', 'Archived',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getFileIcon = (type) => type === 'application/pdf' ? '📄' : '📝';

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatShortDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatCurrency(value) {
  if (value == null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(value);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const ExtractionStatusBadge = ({ status }) => {
  if (status === 'success') return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-medium">
      <CheckCircleIcon className="w-3.5 h-3.5" />Processed
    </span>
  );
  if (status === 'partial') return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full text-xs font-medium">
      <ExclamationTriangleIcon className="w-3.5 h-3.5" />Partial
    </span>
  );
  if (status === 'processing') return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brightblue-50 text-brightblue-700 border border-brightblue-200 rounded-full text-xs font-medium">
      <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />Processing
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-grey-100 text-grey-600 border border-grey-300 rounded-full text-xs font-medium">
      <ClockIcon className="w-3.5 h-3.5" />Uploaded
    </span>
  );
};

const ConfidenceBadge = ({ score }) => {
  if (score == null) return <span className="text-grey-300 text-xs">—</span>;
  const pct = Math.round(score * 100);
  if (pct >= 75) return (
    <span className="inline-flex px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-medium">{pct}%</span>
  );
  if (pct >= 50) return (
    <span className="inline-flex px-2 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-full text-xs font-medium">{pct}%</span>
  );
  return (
    <span className="inline-flex px-2 py-0.5 bg-grey-100 text-grey-500 border border-grey-200 rounded-full text-xs font-medium">{pct}%</span>
  );
};

// Tags cell: shows up to 3 pills + "+N" overflow tooltip
const TagsCell = ({ tags }) => {
  const [showAll, setShowAll] = useState(false);
  if (!tags?.length) return <span className="text-grey-300 text-xs">—</span>;
  const visible  = tags.slice(0, 3);
  const overflow = tags.slice(3);
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visible.map((tag) => (
        <span key={tag} className="px-1.5 py-0.5 bg-brand-50 text-brand-600 border border-brand-200 text-xs rounded-full font-medium whitespace-nowrap">
          {tag}
        </span>
      ))}
      {overflow.length > 0 && (
        <div className="relative group">
          <span className="px-1.5 py-0.5 bg-grey-100 text-grey-500 text-xs rounded-full cursor-default">
            +{overflow.length}
          </span>
          <div className="absolute bottom-full left-0 mb-1.5 z-20 hidden group-hover:block">
            <div className="bg-darkblue-500 text-white text-xs rounded-lg px-2.5 py-2 shadow-lg min-w-max max-w-[200px]">
              <div className="flex flex-wrap gap-1">
                {overflow.map((tag) => (
                  <span key={tag} className="px-1.5 py-0.5 bg-darkblue-400 rounded-full text-xs">{tag}</span>
                ))}
              </div>
              <div className="absolute top-full left-3 border-4 border-transparent border-t-darkblue-500" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Extraction date cell with "Re-extracted" badge
const ExtractionDateCell = ({ extractedAt, wasReExtracted }) => {
  if (!extractedAt) return <span className="text-grey-300 text-xs">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-darkblue-500">{formatShortDate(extractedAt)}</span>
      {wasReExtracted && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-50 text-violet-600 border border-violet-200 rounded-full text-[10px] font-medium w-fit">
          <ArrowPathIcon className="w-2.5 h-2.5" />Re-extracted
        </span>
      )}
    </div>
  );
};

const EngagementCell = ({ engagementId, engagementName }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  if (!engagementId) return (
    <div className="relative inline-block" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
      <LinkIcon className="w-4 h-4 text-grey-300" />
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-darkblue-500 text-white text-xs rounded-lg whitespace-nowrap z-20 shadow-lg">
          No engagement linked
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-darkblue-500" />
        </div>
      )}
    </div>
  );
  return (
    <div className="relative inline-block" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
      <span className="inline-flex items-center justify-center w-7 h-7 bg-brightblue-50 border border-brightblue-200 rounded-lg cursor-default">
        <LinkIcon className="w-3.5 h-3.5 text-brightblue-600" />
      </span>
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-darkblue-500 text-white text-xs rounded-lg whitespace-nowrap z-20 shadow-lg max-w-[180px] truncate">
          {engagementName || 'Linked engagement'}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-darkblue-500" />
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const DocumentManagementTab = () => {
  const [searchTerm,      setSearchTerm]      = useState('');
  const [filterType,      setFilterType]      = useState('all');
  const [filterCategory,  setFilterCategory]  = useState('all');

  const [showPreviewModal,    setShowPreviewModal]    = useState(false);
  const [previewDocumentId,   setPreviewDocumentId]   = useState(null);
  const [showMetadataModal,   setShowMetadataModal]   = useState(false);
  const [pendingUpload,       setPendingUpload]       = useState(null);
  const [editingDoc,          setEditingDoc]          = useState(null);
  const [editName,            setEditName]            = useState('');
  const [editCategory,        setEditCategory]        = useState('');
  const [editTags,            setEditTags]            = useState([]);
  const [editBidStatus,       setEditBidStatus]       = useState('');
  const [editEngagementType,  setEditEngagementType]  = useState('');
  const [showUploadModal,     setShowUploadModal]     = useState(false);
  const [uploadProgress,      setUploadProgress]      = useState(null);
  const [isDragging,          setIsDragging]          = useState(false);
  const [showDeleteConfirm,   setShowDeleteConfirm]   = useState(false);
  const fileInputRef = useRef(null);

  // Extraction state
  const extractMutation = useExtractProposal();
  const [showExtractionModal,       setShowExtractionModal]       = useState(false);
  const [extractionResult,          setExtractionResult]          = useState(null);
  const [extractionEngagementId,    setExtractionEngagementId]    = useState(null);
  const [extractingDocId,           setExtractingDocId]           = useState(null);
  const [extractingDocName,         setExtractingDocName]         = useState(null);
  const [isExtracting,              setIsExtracting]              = useState(false);
  const [extractionComplete,        setExtractionComplete]        = useState(false);
  const [extractionQueueCurrent,    setExtractionQueueCurrent]    = useState(1);
  const [extractionQueueTotal,      setExtractionQueueTotal]      = useState(1);

  // Optimistic updates — local overrides keyed by doc id
  const [optimisticUpdates, setOptimisticUpdates] = useState({});

  // ─── Data ──────────────────────────────────────────────────────────────────

  const { data: documentsData, isLoading, error, refetch, isFetching } = useDocuments({
    category: filterCategory !== 'all' ? filterCategory : undefined,
    search:   searchTerm || undefined,
  });

  const uploadMutation  = useUploadDocument();
  const updateMutation  = useUpdateDocument();
  const deleteMutation  = useDeleteDocument();
  const { downloadDocument } = useDownloadDocument();

  const rawDocuments = documentsData?.documents || [];
  const total        = documentsData?.total || 0;

  // Merge optimistic updates into the document list
  const documents = useMemo(() =>
    rawDocuments.map((doc) =>
      optimisticUpdates[doc.id] ? { ...doc, ...optimisticUpdates[doc.id] } : doc
    ),
    [rawDocuments, optimisticUpdates]
  );

  const filteredDocuments = documents.filter((doc) => {
    if (filterType === 'pdf')  return doc.type === 'application/pdf';
    if (filterType === 'word') return doc.type?.includes('word');
    return true;
  });

  // ─── Optimistic update helper ───────────────────────────────────────────────

  const applyOptimistic = useCallback((docId, updates) => {
    setOptimisticUpdates((prev) => ({
      ...prev,
      [docId]: { ...(prev[docId] || {}), ...updates },
    }));
  }, []);

  // ─── Extraction ─────────────────────────────────────────────────────────────

  const runExtraction = async (docId, docName, engagementId, queueIdx, queueTotal) => {
    setExtractingDocId(docId);
    setExtractingDocName(docName);
    setIsExtracting(true);
    setExtractionComplete(false);
    setExtractionQueueCurrent(queueIdx);
    setExtractionQueueTotal(queueTotal);

    // Optimistic: mark as processing immediately
    applyOptimistic(docId, { extraction_status: 'processing' });

    try {
      const response       = await extractMutation.mutateAsync({ documentId: docId });
      const extractionData = response?.data?.data ?? response?.data ?? response;

      setExtractionComplete(true);
      await new Promise((r) => setTimeout(r, 600));

      // Optimistic: apply extracted fields immediately
      if (extractionData) {
        const ext  = extractionData.extraction  || extractionData?.extraction_result?.extraction;
        const tags = extractionData.tags        || extractionData?.extraction_result?.tags;
        applyOptimistic(docId, {
          extraction_status: extractionData.status || 'success',
          client_name:       ext?.client_name       ?? null,
          bid_amount:        ext?.total_fee          ?? null,
          confidence_score:  extractionData.confidence_score ?? null,
          extracted_tags:    tags?.tags              ?? [],
          extracted_at:      new Date().toISOString(),
          was_re_extracted:  true,  // flag for badge
        });
      }

      return extractionData;
    } catch (err) {
      console.error('Extraction failed:', err);
      applyOptimistic(docId, { extraction_status: 'failed' });
      return null;
    } finally {
      setExtractingDocId(null);
      setExtractingDocName(null);
      setIsExtracting(false);
      setExtractionComplete(false);
    }
  };

  const handleExtract = async (doc) => {
    const isReExtract = doc.extraction_status === 'success' || doc.extraction_status === 'partial';
    // Mark re-extract flag before we start so badge shows correctly
    if (isReExtract) applyOptimistic(doc.id, { was_re_extracted: true });

    const data = await runExtraction(
      doc.id,
      doc.display_name || doc.original_name,
      doc.engagement_id || null,
      1, 1
    );
    if (data) {
      setExtractionResult(data);
      setExtractionEngagementId(doc.engagement_id || null);
      setShowExtractionModal(true);
    } else {
      alert('Failed to process document.');
    }
    await refetch();
  };

  const handleViewExtraction = async (doc) => {
    try {
      const response = await documentsApi.getExtractionResult(doc.id);
      const stored   = response?.data?.data ?? response?.data ?? response;
      let data       = stored?.extraction_result ?? stored;
      if (typeof data === 'string') data = JSON.parse(data);
      setExtractionResult(data);
      setExtractionEngagementId(doc.engagement_id || stored?.engagement_id || null);
      setShowExtractionModal(true);
    } catch (err) {
      console.error('View extraction failed:', err);
      alert('No extraction result found. Process the document first.');
    }
  };

  const handleCloseExtractionModal = () => {
    setShowExtractionModal(false);
    setExtractionResult(null);
    setExtractionEngagementId(null);
  };

  // ─── Upload + sequential auto-extract ──────────────────────────────────────

  const handleMetadataSubmit = async () => {
    if (!pendingUpload) return;
    setShowMetadataModal(false);
    const { files, metadata } = pendingUpload;

    // Determine how many PDFs need extraction for queue counter
    const pdfIndices = files.reduce((acc, f, i) => {
      if (f.type === 'application/pdf') acc.push(i);
      return acc;
    }, []);
    let pdfQueueIdx = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const meta = metadata[i];

      // Upload
      setUploadProgress({ current: i + 1, total: files.length, fileName: meta.displayName || file.name });
      let uploadedDoc = null;
      try {
        const res = await uploadMutation.mutateAsync({
          file,
          displayName:  meta.displayName,
          category:     meta.category,
          engagementId: meta.engagementId || undefined,
        });
        uploadedDoc = res?.data || res;
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        alert(`Failed to upload ${file.name}: ${err.message}`);
        continue;
      } finally {
        setUploadProgress(null);
      }

      // Auto-extract PDFs sequentially with queue position
      if (file.type === 'application/pdf' && uploadedDoc?.id) {
        pdfQueueIdx += 1;
        await runExtraction(
          uploadedDoc.id,
          meta.displayName || file.name,
          meta.engagementId || null,
          pdfQueueIdx,
          pdfIndices.length
        );
      }
    }

    await refetch();
    setPendingUpload(null);
  };

  // ─── Other handlers ─────────────────────────────────────────────────────────

  const handleEditMetadata = (doc) => {
    setEditingDoc(doc);
    setEditName(doc.display_name || doc.original_name);
    setEditCategory(doc.category || 'Other');
    setEditTags(doc.tags || []);
    setEditBidStatus(doc.bid_status || 'Pending');
    setEditEngagementType(doc.engagement_type || 'Project');
    setShowDeleteConfirm(false);
  };

  const handleSaveMetadata = async () => {
    if (!editingDoc) return;
    try {
      await updateMutation.mutateAsync({
        documentId:     editingDoc.id,
        displayName:    editName,
        category:       editCategory,
        tags:           editTags,
        bidStatus:      editBidStatus,
        engagementType: editEngagementType,
      });
      setEditingDoc(null);
    } catch (err) {
      console.error('Error updating metadata:', err);
      alert('Failed to update document. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!editingDoc) return;
    try {
      await deleteMutation.mutateAsync(editingDoc.id);
      setEditingDoc(null);
      setShowDeleteConfirm(false);
    } catch (err) {
      alert('Failed to delete document. Please try again.');
    }
  };

  const handlePreview  = (doc) => {
    if (doc.type !== 'application/pdf') { alert('Only PDF documents can be previewed'); return; }
    setPreviewDocumentId(doc.id);
    setShowPreviewModal(true);
  };

  const handleDownload = async (doc) => {
    try { await downloadDocument(doc.id, doc.display_name || doc.original_name); }
    catch { alert('Failed to download document. Please try again.'); }
  };

  // ─── File input ─────────────────────────────────────────────────────────────

  const handleDragOver   = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave  = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop       = (e) => { e.preventDefault(); setIsDragging(false); handleFiles(Array.from(e.dataTransfer.files)); };
  const handleFileSelect = (e) => handleFiles(Array.from(e.target.files));

  const handleFiles = (files, presetEngagementId = null) => {
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    const validFiles = [];
    const errors     = [];
    for (const file of files) {
      if (!validTypes.includes(file.type)) { errors.push(`${file.name}: Invalid type`); continue; }
      if (file.size > 10 * 1024 * 1024)   { errors.push(`${file.name}: Too large (max 10MB)`); continue; }
      validFiles.push(file);
    }
    if (errors.length) alert('Some files were not added:\n\n' + errors.join('\n'));
    if (validFiles.length) {
      setPendingUpload({
        files:    validFiles,
        metadata: validFiles.map((f) => ({
          originalName: f.name,
          displayName:  f.name.replace(/\.[^/.]+$/, ''),
          category:     'Other',
          engagementId: presetEngagementId || null,
        })),
      });
      setShowMetadataModal(true);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── Columns ────────────────────────────────────────────────────────────────

  const columns = useMemo(() => [
    {
      key: 'display_name', label: 'Document', searchable: true,
      render: (value, row) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-semibold text-darkblue-500 text-sm leading-snug truncate">
            {value || row.original_name}
          </span>
          <span className="text-xs text-grey-500 flex items-center gap-2">
            <span>{formatFileSize(row.size)}</span>
            <span className="text-grey-300">·</span>
            <span>{formatDate(row.uploaded_at)}</span>
          </span>
        </div>
      ),
    },
    {
      key: 'client_name', label: 'Client', searchable: false,
      render: (value) => value
        ? <span className="text-sm text-darkblue-500 font-medium">{value}</span>
        : <span className="text-grey-300 text-xs">—</span>,
    },
    {
      key: 'category', label: 'Category', searchable: false,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-1 bg-brand-50 text-brand-600 border border-brand-200 rounded-full text-xs font-medium whitespace-nowrap">
          {value || 'Other'}
        </span>
      ),
    },
    {
      key: 'bid_amount', label: 'Bid Amount', searchable: false,
      render: (value) => value != null
        ? <span className="text-sm font-medium text-darkblue-500">{formatCurrency(value)}</span>
        : <span className="text-grey-300 text-xs">—</span>,
    },
    {
      key: 'extracted_tags', label: 'Tags', sortable: false, searchable: false,
      render: (value) => <TagsCell tags={value} />,
    },
    {
      key: 'engagement_id', label: 'Engagement', sortable: false, searchable: false,
      render: (value, row) => <EngagementCell engagementId={value} engagementName={row.engagement_name} />,
    },
    {
      key: 'extraction_status', label: 'Status', searchable: false,
      render: (value) => <ExtractionStatusBadge status={value} />,
    },
    {
      key: 'confidence_score', label: 'Confidence', searchable: false,
      render: (value) => <ConfidenceBadge score={value} />,
    },
    {
      key: 'extracted_at', label: 'Extracted', searchable: false,
      render: (value, row) => (
        <ExtractionDateCell
          extractedAt={value}
          wasReExtracted={row.was_re_extracted}
        />
      ),
    },
    {
      key: 'id', label: 'Actions', sortable: false, searchable: false, align: 'right',
      render: (_, doc) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => handleEditMetadata(doc)} className="p-1.5 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors" title="Edit">
            <PencilIcon className="w-4 h-4" />
          </button>
          {doc.type === 'application/pdf' && (
            <>
              {(doc.extraction_status === 'success' || doc.extraction_status === 'partial') ? (
                <>
                  <button onClick={() => handleViewExtraction(doc)} className="p-1.5 text-brightblue-600 hover:bg-brightblue-50 rounded-lg transition-colors" title="View extraction">
                    <DocumentMagnifyingGlassIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleExtract(doc)} disabled={extractingDocId === doc.id} className="p-1.5 text-violet-500 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-40" title="Re-run extraction">
                    {extractingDocId === doc.id
                      ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      : <ArrowPathIcon className="w-4 h-4" />}
                  </button>
                </>
              ) : (
                <button onClick={() => handleExtract(doc)} disabled={extractingDocId === doc.id} className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-40" title="Process with AI">
                  {extractingDocId === doc.id
                    ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    : <SparklesIcon className="w-4 h-4" />}
                </button>
              )}
              <button onClick={() => handlePreview(doc)} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Preview PDF">
                <EyeIcon className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={() => handleDownload(doc)} className="p-1.5 text-grey-400 hover:text-grey-600 hover:bg-grey-100 rounded-lg transition-colors" title="Download">
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [extractingDocId]);

  // ─── Filters ────────────────────────────────────────────────────────────────

  const customFiltersSlot = (
    <div className="flex flex-wrap items-center gap-3">
      <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
        className="px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 focus:ring-2 focus:ring-brand-500 outline-none bg-white">
        <option value="all">All Types</option>
        <option value="pdf">PDF Only</option>
        <option value="word">Word Only</option>
      </select>
      <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
        className="px-3 py-2 border border-grey-300 rounded-lg text-sm text-darkblue-500 focus:ring-2 focus:ring-brand-500 outline-none bg-white">
        <option value="all">All Categories</option>
        {DOCUMENT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
      </select>
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-darkblue-500">Document Management</h2>
          <p className="text-sm text-grey-500 mt-0.5">Upload and manage bid documents</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowUploadModal(true)} disabled={uploadMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 transition-colors font-medium text-sm disabled:opacity-50">
            <DocumentArrowUpIcon className="w-4 h-4" />Upload
          </button>
          <button onClick={() => refetch()} disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-grey-600 bg-white border border-grey-300 rounded-lg hover:bg-grey-50 transition-colors disabled:opacity-50">
            <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <div className="text-right pl-2 border-l border-grey-200">
            <div className="text-xl font-bold text-brand-600">{total}</div>
            <div className="text-xs text-grey-500 leading-none">Documents</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-pink-700">
            <ExclamationCircleIcon className="w-5 h-5 shrink-0" />
            <span className="font-medium text-sm">Error loading documents</span>
          </div>
          <p className="text-sm text-pink-600 mt-1">{error.message}</p>
          <button onClick={() => refetch()} className="mt-2 text-sm text-pink-700 underline">Try again</button>
        </div>
      )}

      {!error && (
        <DataTable
          data={filteredDocuments}
          columns={columns}
          loading={isLoading}
          defaultSort={{ key: 'uploaded_at', direction: 'desc' }}
          defaultItemsPerPage={25}
          showSearch={true}
          showFilters={false}
          showPagination={true}
          emptyMessage="No documents yet. Upload your first document to get started."
          emptyIcon={<DocumentTextIcon className="w-12 h-12 text-grey-300 mx-auto" />}
          customFilters={customFiltersSlot}
        />
      )}

      {/* ── Upload Modal ── */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200">
              <h3 className="text-base font-semibold text-darkblue-500">Upload Documents</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                onDrop={(e) => { handleDrop(e); setShowUploadModal(false); }}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${
                  isDragging ? 'border-brand-500 bg-brand-50' : 'border-grey-300 hover:border-brand-400 hover:bg-grey-50'
                }`}>
                <DocumentArrowUpIcon className="w-12 h-12 text-grey-300 mx-auto mb-3" />
                <p className="font-semibold text-darkblue-500 mb-1">Drop files here or click to upload</p>
                <p className="text-sm text-grey-500 mb-1">PDF and Word documents · max 10MB each</p>
                <p className="text-xs text-grey-400 mb-2">You can select multiple files at once</p>
                <p className="text-xs text-teal-600 mb-4 flex items-center justify-center gap-1">
                  <SparklesIcon className="w-3.5 h-3.5" />PDFs are automatically extracted after upload
                </p>
                <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx"
                  onChange={(e) => { handleFileSelect(e); setShowUploadModal(false); }}
                  className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploadMutation.isLoading}
                  className="px-5 py-2.5 bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 transition-colors font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2">
                  <DocumentArrowUpIcon className="w-4 h-4" />Select Files
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex justify-end rounded-b-xl">
              <button onClick={() => setShowUploadModal(false)} className="px-4 py-2 text-sm text-grey-600 hover:bg-grey-100 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Metadata Modal ── */}
      {showMetadataModal && pendingUpload && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200 sticky top-0 bg-white">
              <h3 className="text-base font-semibold text-darkblue-500">
                Document Details
                <span className="ml-2 px-2 py-0.5 bg-brand-50 text-brand-600 text-xs rounded-full border border-brand-200">
                  {pendingUpload.files.length} file{pendingUpload.files.length > 1 ? 's' : ''}
                </span>
              </h3>
              <button onClick={() => { setShowMetadataModal(false); setPendingUpload(null); }}
                className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {pendingUpload.files.map((file, index) => (
                <div key={index} className="border border-grey-200 rounded-xl p-4 bg-grey-50">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-2xl">{getFileIcon(file.type)}</span>
                    <div>
                      <div className="font-medium text-darkblue-500 text-sm">{file.name}</div>
                      <div className="text-xs text-grey-500">{formatFileSize(file.size)}</div>
                      {file.type === 'application/pdf' && (
                        <div className="text-xs text-teal-600 flex items-center gap-1 mt-0.5">
                          <SparklesIcon className="w-3 h-3" />Will be auto-extracted
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Display Name</label>
                      <input type="text" value={pendingUpload.metadata[index].displayName}
                        onChange={(e) => {
                          const m = [...pendingUpload.metadata];
                          m[index].displayName = e.target.value;
                          setPendingUpload({ ...pendingUpload, metadata: m });
                        }}
                        className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Category</label>
                      <select value={pendingUpload.metadata[index].category}
                        onChange={(e) => {
                          const m = [...pendingUpload.metadata];
                          m[index].category = e.target.value;
                          setPendingUpload({ ...pendingUpload, metadata: m });
                        }}
                        className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-white">
                        {DOCUMENT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex gap-3 sticky bottom-0 rounded-b-xl">
              <button onClick={() => { setShowMetadataModal(false); setPendingUpload(null); }}
                className="flex-1 px-4 py-2.5 border border-grey-300 text-grey-600 rounded-lg hover:bg-white text-sm font-medium">
                Cancel
              </button>
              <button onClick={handleMetadataSubmit} disabled={uploadMutation.isLoading}
                className="flex-1 px-4 py-2.5 bg-darkblue-500 text-white rounded-lg hover:bg-darkblue-600 text-sm font-medium disabled:opacity-50">
                Upload {pendingUpload.files.length} Document{pendingUpload.files.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Metadata Modal ── */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto relative">
            {updateMutation.isLoading && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm text-darkblue-500 font-medium">Saving changes...</p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200 sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-base font-semibold text-darkblue-500">Edit Document</h3>
              <button onClick={() => setEditingDoc(null)} disabled={updateMutation.isLoading}
                className="p-1.5 text-grey-400 hover:bg-grey-100 rounded-lg disabled:opacity-50">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Display Name</label>
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                  disabled={updateMutation.isLoading}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-grey-50" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Category</label>
                <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)}
                  disabled={updateMutation.isLoading}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-grey-50">
                  {DOCUMENT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Bid Status</label>
                  <select value={editBidStatus} onChange={(e) => setEditBidStatus(e.target.value)}
                    disabled={updateMutation.isLoading}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-grey-50">
                    {BID_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Engagement Type</label>
                  <select value={editEngagementType} onChange={(e) => setEditEngagementType(e.target.value)}
                    disabled={updateMutation.isLoading}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none disabled:bg-grey-50">
                    {ENGAGEMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Tags</label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border border-grey-300 rounded-lg bg-grey-50">
                    {editTags.length === 0
                      ? <span className="text-grey-400 text-xs self-center">No tags selected</span>
                      : editTags.map((tag) => (
                          <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-brightblue-50 text-brightblue-600 border border-brightblue-200 rounded-full text-xs">
                            {tag}
                            <button onClick={() => setEditTags(editTags.filter((t) => t !== tag))}
                              className="hover:bg-brightblue-100 rounded-full p-0.5">
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </span>
                        ))
                    }
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {AVAILABLE_TAGS.filter((tag) => !editTags.includes(tag)).map((tag) => (
                      <button key={tag} onClick={() => setEditTags([...editTags, tag])}
                        className="px-2.5 py-1 bg-grey-100 text-grey-600 border border-grey-200 rounded-full text-xs hover:bg-grey-200 transition-colors">
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-xs text-grey-400 bg-grey-50 px-3 py-2 rounded-lg border border-grey-200">
                <span className="font-medium text-grey-500">Original file:</span> {editingDoc.original_name}
              </div>
              {/* Extracted data summary */}
              {(editingDoc.client_name || editingDoc.bid_amount != null) && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2.5 space-y-1">
                  <p className="text-xs font-semibold text-teal-700 flex items-center gap-1">
                    <SparklesIcon className="w-3.5 h-3.5" />Extracted Data
                  </p>
                  {editingDoc.client_name && <p className="text-xs text-teal-600">Client: <span className="font-medium">{editingDoc.client_name}</span></p>}
                  {editingDoc.bid_amount != null && <p className="text-xs text-teal-600">Bid: <span className="font-medium">{formatCurrency(editingDoc.bid_amount)}</span></p>}
                  {editingDoc.confidence_score != null && <p className="text-xs text-teal-600">Confidence: <span className="font-medium">{Math.round(editingDoc.confidence_score * 100)}%</span></p>}
                </div>
              )}
              <div className="pt-2 mt-2 border-t border-grey-200">
                {!showDeleteConfirm ? (
                  <button onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 text-xs text-grey-400 hover:text-pink-600 transition-colors">
                    <TrashIcon className="w-3.5 h-3.5" />Delete this document
                  </button>
                ) : (
                  <div className="flex items-center gap-3 bg-pink-50 border border-pink-200 rounded-lg px-3 py-2.5">
                    <span className="text-xs text-pink-700 flex-1">
                      Delete <strong>"{editingDoc.display_name || editingDoc.original_name}"</strong>? This cannot be undone.
                    </span>
                    <button onClick={() => setShowDeleteConfirm(false)} className="text-xs text-grey-500 hover:text-grey-700 px-2 py-1 rounded">Cancel</button>
                    <button onClick={handleDelete} disabled={deleteMutation.isLoading}
                      className="text-xs font-medium text-white bg-pink-600 hover:bg-pink-700 px-3 py-1.5 rounded-lg flex items-center gap-1">
                      {deleteMutation.isLoading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <TrashIcon className="w-3 h-3" />}
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex gap-3 sticky bottom-0 rounded-b-xl">
              <button onClick={() => setEditingDoc(null)} disabled={updateMutation.isLoading}
                className="flex-1 px-4 py-2.5 border border-grey-300 text-grey-600 rounded-lg hover:bg-white text-sm font-medium disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleSaveMetadata} disabled={!editName.trim() || updateMutation.isLoading}
                className="flex-1 px-4 py-2.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {updateMutation.isLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      {showPreviewModal && previewDocumentId && (
        <PreviewModal
          documentId={previewDocumentId}
          onClose={() => { setShowPreviewModal(false); setPreviewDocumentId(null); }}
        />
      )}

      {/* ── Extraction Result Modal ── */}
      {showExtractionModal && extractionResult && (
        <ExtractionResultModal
          result={extractionResult}
          engagementId={extractionEngagementId}
          onClose={handleCloseExtractionModal}
          onApplied={() => refetch()}
        />
      )}

      {/* ── Upload Progress ── */}
      {uploadProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full text-center">
            <div className="w-14 h-14 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h3 className="text-base font-semibold text-darkblue-500 mb-1">Uploading Document</h3>
            <p className="text-sm text-grey-500 mb-4 truncate px-4">{uploadProgress.fileName}</p>
            {uploadProgress.total > 1 && (
              <>
                <div className="w-full bg-grey-200 rounded-full h-1.5 mb-2">
                  <div className="bg-brand-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
                </div>
                <p className="text-xs text-grey-400">{uploadProgress.current} of {uploadProgress.total} files</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Extraction Progress Modal ── */}
      {isExtracting && (
        <ExtractionProgressModal
          documentName={extractingDocName}
          isVisible={isExtracting}
          isComplete={extractionComplete}
          currentDoc={extractionQueueCurrent}
          totalDocs={extractionQueueTotal}
        />
      )}
    </div>
  );
};

// ─── Preview Modal ─────────────────────────────────────────────────────────────

const PreviewModal = ({ documentId, onClose }) => {
  const { data: previewData, isLoading, error } = useDocumentPreview(documentId, true);
  const { data: documentsData } = useDocuments({});
  const signedUrl  = previewData?.signedUrl;
  const document   = documentsData?.documents?.find((d) => d.id === documentId);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex items-center justify-between px-6 py-3 bg-darkblue-500 border-b border-darkblue-600">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xl">📄</span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">
              {document?.display_name || document?.original_name || 'Document Preview'}
            </h3>
            {document && (
              <p className="text-xs text-grey-300">{formatFileSize(document.size)} · PDF · {document.category}</p>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-2 text-grey-300 hover:text-white hover:bg-darkblue-600 rounded-lg transition-colors">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-hidden bg-grey-900">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white text-sm">Loading preview...</p>
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <ExclamationCircleIcon className="w-16 h-16 text-pink-500 mx-auto mb-4" />
              <h4 className="text-base font-semibold text-white mb-2">Preview Failed</h4>
              <p className="text-grey-400 text-sm">{error.message}</p>
            </div>
          </div>
        )}
        {signedUrl && !isLoading && (
          <iframe src={signedUrl} className="w-full h-full" title="Document Preview" />
        )}
      </div>
    </div>
  );
};

export default DocumentManagementTab;